'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import { CAPABILITIES } from '../_lib/capabilities';
import { CAPABILITY_ICONS, CloseIcon, ComposerIcon, HomeIcon } from './icons';

type MobileDrawerProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Mobile capability drawer (opened by the gh-bar hamburger on < 768 px).
 * Lists Hub · 5 capabilities · Composer; the current capability is
 * highlighted. Closes on selection or scrim tap.
 */
export function MobileDrawer({ open, onClose }: MobileDrawerProps) {
  const pathname = usePathname() ?? '/';

  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open, onClose]);

  if (!open) return null;

  const onComposer = pathname === '/composer' || pathname.startsWith('/composer/');
  const onHub = pathname === '/';

  return (
    <>
      <div className="mnav-scrim" onClick={onClose} aria-hidden />
      <nav className="mnav" aria-label="Navigation">
        <div className="mnav-head">
          <Link href="/" className="mnav-wordmark" onClick={onClose}>
            mh.
          </Link>
          <button type="button" className="mnav-close" aria-label="Close menu" onClick={onClose}>
            {CloseIcon}
          </button>
        </div>

        <Link
          href="/"
          className={`mnav-item${onHub ? ' active' : ''}`}
          onClick={onClose}
        >
          <span className="ic">{HomeIcon}</span>
          <span className="body">
            <span className="t">Hub</span>
            <span className="s">Today, Growth, Project</span>
          </span>
        </Link>

        <div className="mnav-section-label">Capabilities</div>
        {CAPABILITIES.map((cap) => {
          const active = pathname === cap.href || pathname.startsWith(cap.href + '/');
          return (
            <Link
              key={cap.id}
              href={cap.href}
              className={`mnav-item${active ? ' active' : ''}`}
              onClick={onClose}
            >
              <span className="ic">{CAPABILITY_ICONS[cap.id]}</span>
              <span className="body">
                <span className="t">{cap.label}</span>
                <span className="s">{cap.subtext}</span>
              </span>
            </Link>
          );
        })}

        <div className="mnav-divider" />
        <Link
          href="/composer"
          className={`mnav-item${onComposer ? ' active' : ''}`}
          onClick={onClose}
        >
          <span className="ic">{ComposerIcon}</span>
          <span className="body">
            <span className="t">Composer</span>
            <span className="s">Chat with Hunter</span>
          </span>
        </Link>
      </nav>
    </>
  );
}
